import { useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  Settings as SettingsIcon,
  Users,
  Webhook,
  Clock,
  Layers,
  Code2,
  KeyRound,
  Copy,
  Check,
  Plug,
  ExternalLink,
  Shield,
  Trash2,
  Plus,
  CreditCard,
} from 'lucide-react'
import { Loader2 } from 'lucide-react'
import { PageHeader } from '@/components/PageHeader'
import { BillingPanel } from '@/features/settings/BillingPanel'
import { useStore } from '@/lib/store'
import {
  listApiKeys,
  createApiKey,
  revokeApiKey,
  getWorkspaceInfo,
  updateWorkspaceInfo,
  listWebhooks,
  createWebhook,
  deleteWebhook,
  type ApiKey,
  type WebhookEndpoint,
  type WorkspaceInfo,
} from '@/lib/data'

/* ------------------------------------------------------------------ */
/* Reusable bits                                                       */
/* ------------------------------------------------------------------ */

type TabId = 'Global' | 'Billing' | 'Team' | 'Webhooks' | 'Auto Post' | 'Developers'

const TABS: { id: TabId; label: string; icon: ReactNode }[] = [
  { id: 'Global', label: 'Global', icon: <SettingsIcon className="h-4 w-4" /> },
  { id: 'Billing', label: 'Billing', icon: <CreditCard className="h-4 w-4" /> },
  { id: 'Team', label: 'Team', icon: <Users className="h-4 w-4" /> },
  { id: 'Webhooks', label: 'Webhooks', icon: <Webhook className="h-4 w-4" /> },
  { id: 'Auto Post', label: 'Auto Post', icon: <Clock className="h-4 w-4" /> },
  { id: 'Developers', label: 'Developers', icon: <Code2 className="h-4 w-4" /> },
]

function useCopied(timeout = 1500) {
  const [copied, setCopied] = useState(false)
  const copy = (text: string) => {
    void navigator.clipboard?.writeText(text).catch(() => {})
    setCopied(true)
    window.setTimeout(() => setCopied(false), timeout)
  }
  return { copied, copy }
}

function CopyButton({
  value,
  label = 'Copy',
  className = '',
}: {
  value: string
  label?: string
  className?: string
}) {
  const { copied, copy } = useCopied()
  return (
    <button
      type="button"
      onClick={() => copy(value)}
      className={`btn-subtle px-2.5 py-1.5 text-xs ${className}`}
      aria-label={label}
    >
      {copied ? (
        <>
          <Check className="h-3.5 w-3.5 text-accent-green" /> Copied
        </>
      ) : (
        <>
          <Copy className="h-3.5 w-3.5" /> {label}
        </>
      )}
    </button>
  )
}

function Toggle({
  checked,
  onChange,
  label,
  description,
}: {
  checked: boolean
  onChange: (next: boolean) => void
  label: string
  description?: string
}) {
  return (
    <label className="flex cursor-pointer items-start justify-between gap-4 py-3">
      <span className="min-w-0">
        <span className="block text-sm font-medium text-ink-800">{label}</span>
        {description && <span className="mt-0.5 block text-xs text-ink-400">{description}</span>}
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors duration-200 ${
          checked ? 'bg-brand-500' : 'bg-ink-200'
        }`}
      >
        <span
          className={`inline-block h-4.5 w-4.5 transform rounded-full bg-white shadow transition-transform duration-200 ${
            checked ? 'translate-x-5' : 'translate-x-1'
          }`}
          style={{ height: '1.125rem', width: '1.125rem' }}
        />
      </button>
    </label>
  )
}

function CodeBlock({
  code,
  copyValue,
  copyLabel = 'Copy',
  actions,
}: {
  code: ReactNode
  copyValue: string
  copyLabel?: string
  actions?: ReactNode
}) {
  return (
    <div className="relative overflow-hidden rounded-xl border border-line bg-panel">
      <div className="absolute right-2 top-2 z-10 flex items-center gap-1">
        {actions}
        <CopyButton value={copyValue} label={copyLabel} className="bg-surface border border-line" />
      </div>
      <pre className="overflow-x-auto p-4 pr-28 font-mono text-[12.5px] leading-relaxed text-ink-700">
        {code}
      </pre>
    </div>
  )
}

function SectionTitle({
  icon,
  title,
  subtitle,
}: {
  icon?: ReactNode
  title: string
  subtitle?: string
}) {
  return (
    <div className="mb-3 flex items-start gap-2.5">
      {icon && <span className="mt-0.5 text-brand-500">{icon}</span>}
      <div>
        <h3 className="font-display text-sm font-semibold tracking-tight text-ink-900">{title}</h3>
        {subtitle && <p className="mt-0.5 text-xs text-ink-400">{subtitle}</p>}
      </div>
    </div>
  )
}

function PanelHeading({ title, description }: { title: string; description?: string }) {
  return (
    <div className="mb-5">
      <h2 className="font-display text-lg font-bold tracking-tight text-ink-900">{title}</h2>
      {description && <p className="mt-1 text-sm text-ink-500">{description}</p>}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Panels                                                              */
/* ------------------------------------------------------------------ */

function GlobalPanel() {
  const settings = useStore((s) => s.settings)
  const updateSettings = useStore((s) => s.updateSettings)
  const [workspace, setWorkspace] = useState('')
  const [timezone, setTimezone] = useState(settings.timezone)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    getWorkspaceInfo()
      .then((info) => {
        if (!alive) return
        setWorkspace(info.workspace.name)
        setTimezone(info.workspace.timezone)
        updateSettings({ timezone: info.workspace.timezone })
      })
      .catch((e) => {
        if (alive) setError(e instanceof Error ? e.message : 'Failed to load workspace')
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
    }
  }, [updateSettings])

  const save = async () => {
    setSaving(true)
    setSaved(false)
    setError(null)
    try {
      const updated = await updateWorkspaceInfo({ name: workspace, timezone })
      setWorkspace(updated.name)
      setTimezone(updated.timezone)
      updateSettings({ timezone: updated.timezone })
      setSaved(true)
      window.setTimeout(() => setSaved(false), 1500)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save workspace')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div>
      <PanelHeading title="Global" description="Workspace defaults applied across Hey Tasky." />
      <div className="card space-y-5 p-5">
        {loading && (
          <p className="flex items-center gap-2 text-sm text-ink-400">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading workspace...
          </p>
        )}
        <div>
          <label className="mb-1.5 block text-sm font-medium text-ink-800">Workspace name</label>
          <input
            className="input"
            value={workspace}
            onChange={(e) => setWorkspace(e.target.value)}
            placeholder="My workspace"
          />
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-medium text-ink-800">Timezone</label>
          <input
            className="input"
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            placeholder="e.g. America/New_York"
          />
          <p className="mt-1.5 text-xs text-ink-400">
            Scheduling and analytics use this timezone.
          </p>
        </div>
        <div className="border-t border-line pt-1">
          <Toggle
            checked={settings.weekStartsMonday}
            onChange={(v) => updateSettings({ weekStartsMonday: v })}
            label="Week starts on Monday"
            description="Device preference for the calendar grid layout."
          />
        </div>
        {error && <p className="text-xs text-accent-red">{error}</p>}
      </div>

      <div className="card mt-4 flex items-center gap-3 p-4">
        <span className="grid h-9 w-9 place-items-center rounded-lg bg-brand-50 text-brand-500">
          <Layers className="h-4 w-4" />
        </span>
        <p className="text-xs text-ink-500">
          Hey Tasky uses a clean light theme tuned for focused scheduling sessions.
        </p>
      </div>

      <div className="mt-5 flex items-center gap-3">
        <button type="button" className="btn-primary" onClick={save} disabled={saving || loading}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          Save changes
        </button>
        {saved && (
          <span className="flex items-center gap-1.5 text-sm text-accent-green">
            <Check className="h-4 w-4" /> Saved
          </span>
        )}
      </div>
    </div>
  )
}

function TeamPanel() {
  const [info, setInfo] = useState<WorkspaceInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    getWorkspaceInfo()
      .then((next) => alive && setInfo(next))
      .catch((e) => alive && setError(e instanceof Error ? e.message : 'Failed to load team'))
      .finally(() => alive && setLoading(false))
    return () => {
      alive = false
    }
  }, [])

  return (
    <div>
      <PanelHeading title="Team" description="Manage who can access this workspace." />

      <div className="card p-2">
        {loading ? (
          <p className="flex items-center gap-2 p-3 text-sm text-ink-400">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading members...
          </p>
        ) : error ? (
          <p className="p-3 text-sm text-accent-red">{error}</p>
        ) : (
          info?.members.map((member) => (
            <div key={member.id} className="flex items-center justify-between gap-3 rounded-xl px-3 py-3">
              <div className="flex items-center gap-3">
                <span className="grid h-9 w-9 place-items-center rounded-full bg-brand-500 text-sm font-bold text-white">
                  {(member.name || member.email).slice(0, 1).toUpperCase()}
                </span>
                <div>
                  <p className="text-sm font-medium text-ink-800">{member.email}</p>
                  <p className="text-xs text-ink-400">{member.name || 'Workspace member'}</p>
                </div>
              </div>
              <span className="chip bg-brand-50 text-brand-600">
                <Shield className="h-3 w-3" /> {member.role}
              </span>
            </div>
          ))
        )}
      </div>

      <div className="card mt-4 p-5">
        <SectionTitle title="Team invitations" subtitle="Invite flow is not enabled for this workspace." />
        <p className="text-sm text-ink-500">
          Current access is limited to the members shown above.
        </p>
      </div>
    </div>
  )
}

const WEBHOOK_EVENTS = ['post.published', 'post.failed', 'post.scheduled', 'channel.connected']

function WebhooksPanel() {
  const [hooks, setHooks] = useState<WebhookEndpoint[]>([])
  const [url, setUrl] = useState('')
  const [selected, setSelected] = useState<string[]>(['post.published'])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [encryptionReady, setEncryptionReady] = useState(false)
  const [freshSecret, setFreshSecret] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const reload = () => {
    setLoading(true)
    setError(null)
    listWebhooks()
      .then((data) => {
        setHooks(data.webhooks)
        setEncryptionReady(data.encryptionReady)
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load webhooks'))
      .finally(() => setLoading(false))
  }

  useEffect(reload, [])

  const toggleEvent = (ev: string) =>
    setSelected((prev) => (prev.includes(ev) ? prev.filter((e) => e !== ev) : [...prev, ev]))

  const add = async () => {
    const trimmed = url.trim()
    if (!trimmed || selected.length === 0) return
    setSaving(true)
    setError(null)
    setFreshSecret(null)
    try {
      const created = await createWebhook({ url: trimmed, events: selected })
      setHooks((prev) => [created.webhook, ...prev])
      setFreshSecret(created.signingSecret)
      setUrl('')
      setSelected(['post.published'])
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to add webhook')
    } finally {
      setSaving(false)
    }
  }

  const remove = async (id: string) => {
    const previous = hooks
    setHooks((prev) => prev.filter((hook) => hook.id !== id))
    try {
      await deleteWebhook(id)
    } catch (e) {
      setHooks(previous)
      setError(e instanceof Error ? e.message : 'Failed to delete webhook')
    }
  }

  return (
    <div>
      <PanelHeading
        title="Webhooks"
        description="Receive real-time HTTP callbacks when events happen in your workspace."
      />

      <div className="card p-5">
        <SectionTitle title="Add webhook" subtitle="We'll POST a JSON payload to this endpoint." />
        {!encryptionReady && !loading && (
          <div className="mb-3 rounded-xl border border-accent-amber/40 bg-accent-amber/10 p-3 text-xs text-ink-700">
            Webhook signing secrets require server encryption. Configure TOKEN_ENCRYPTION_KEY before
            adding endpoints.
          </div>
        )}
        <input
          className="input mb-3"
          value={url}
          placeholder="https://example.com/webhooks/heytasky"
          onChange={(e) => setUrl(e.target.value)}
        />
        <p className="mb-2 text-xs font-medium text-ink-500">Events</p>
        <div className="mb-4 flex flex-wrap gap-2">
          {WEBHOOK_EVENTS.map((ev) => {
            const on = selected.includes(ev)
            return (
              <button
                key={ev}
                type="button"
                onClick={() => toggleEvent(ev)}
                className={`chip border transition ${
                  on
                    ? 'border-brand-200 bg-brand-50 text-brand-600'
                    : 'border-line bg-surface text-ink-500 hover:text-ink-900'
                }`}
              >
                {on && <Check className="h-3 w-3" />}
                {ev}
              </button>
            )
          })}
        </div>
        <button
          type="button"
          className="btn-primary"
          onClick={add}
          disabled={saving || !encryptionReady}
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          Add webhook
        </button>
        {error && <p className="mt-3 text-xs text-accent-red">{error}</p>}
        {freshSecret && (
          <div className="mt-3 rounded-xl border border-brand-200 bg-brand-50 p-3.5">
            <p className="mb-2 text-xs font-medium text-brand-700">
              Copy this signing secret now. You will not be able to see it again.
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 truncate rounded-lg border border-brand-200 bg-surface px-3 py-2 font-mono text-sm text-ink-800">
                {freshSecret}
              </code>
              <CopyButton value={freshSecret} label="Copy" className="btn-ghost px-3 py-2" />
            </div>
          </div>
        )}
      </div>

      <div className="mt-4 space-y-3">
        {loading ? (
          <p className="flex items-center gap-2 text-sm text-ink-400">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading webhooks...
          </p>
        ) : hooks.length === 0 ? (
          <p className="text-sm text-ink-400">No webhooks configured yet.</p>
        ) : (
          hooks.map((h) => (
            <div key={h.id} className="card flex items-start justify-between gap-3 p-4">
              <div className="min-w-0">
                <p className="truncate font-mono text-sm text-ink-800">{h.url}</p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {h.events.map((ev) => (
                    <span key={ev} className="chip bg-panel border border-line text-xs text-ink-500">
                      {ev}
                    </span>
                  ))}
                </div>
              </div>
              <button
                type="button"
                aria-label="Delete webhook"
                className="btn-subtle shrink-0 px-2 py-2 text-ink-400 hover:text-accent-red"
                onClick={() => remove(h.id)}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  )
}

function AutoPostPanel() {
  return (
    <div>
      <PanelHeading title="Auto Post" description="How scheduled content is published." />
      <div className="card space-y-4 p-5">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 h-2.5 w-2.5 rounded-full bg-accent-green" />
          <div>
            <p className="text-sm font-medium text-ink-800">Scheduled publishing is active</p>
            <p className="mt-0.5 text-xs text-ink-400">
              Posts scheduled in the composer are picked up by the publishing worker when their
              run time arrives.
            </p>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <span className="mt-0.5 h-2.5 w-2.5 rounded-full bg-accent-green" />
          <div>
            <p className="text-sm font-medium text-ink-800">Retry handling is automatic</p>
            <p className="mt-0.5 text-xs text-ink-400">
              Retryable network errors are re-queued with backoff. Auth failures mark the channel
              for reconnect.
            </p>
          </div>
        </div>
        <div className="rounded-xl border border-line bg-panel p-3 text-xs text-ink-500">
          Email publish notifications are not enabled for this workspace.
        </div>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Developers (the important one)                                      */
/* ------------------------------------------------------------------ */

type McpClient = 'Claude Code' | 'ChatGPT' | 'Cursor' | 'VS Code' | 'Codex'
const MCP_CLIENTS: McpClient[] = ['Claude Code', 'ChatGPT', 'Cursor', 'VS Code', 'Codex']
type McpAuth = 'OAuth' | 'API key'

const MCP_URL = (import.meta.env.VITE_MCP_URL as string) || 'https://mcp.heytasky.com/mcp'

function fmtDate(ms: number | null): string {
  if (!ms) return 'never'
  return new Date(ms).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

function DevelopersPanel() {
  const channels = useStore((s) => s.channels)

  const [client, setClient] = useState<McpClient>('Claude Code')
  const [authMode, setAuthMode] = useState<McpAuth>('OAuth')

  // Live API keys from the worker.
  const [keys, setKeys] = useState<ApiKey[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [newName, setNewName] = useState('')
  const [creating, setCreating] = useState(false)
  const [fresh, setFresh] = useState<(ApiKey & { key: string }) | null>(null)

  useEffect(() => {
    let alive = true
    listApiKeys()
      .then((k) => alive && setKeys(k))
      .catch((e) => alive && setError(e.message))
      .finally(() => alive && setLoading(false))
    return () => {
      alive = false
    }
  }, [])

  const create = async () => {
    setCreating(true)
    setError(null)
    try {
      const created = await createApiKey(newName.trim() || 'API key')
      setFresh(created)
      setKeys((prev) => [created, ...prev])
      setNewName('')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create key')
    } finally {
      setCreating(false)
    }
  }

  const revoke = async (id: string) => {
    try {
      await revokeApiKey(id)
      setKeys((prev) => prev.filter((k) => k.id !== id))
      if (fresh?.id === id) setFresh(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to revoke key')
    }
  }

  // MCP connect snippets. OAuth is the default door (the client runs the browser
  // sign-in flow, no secret to paste). The API-key door is for headless tooling.
  const sampleKey = fresh?.key || 'YOUR_API_KEY'
  const oauthClaude = `claude mcp add --transport http heytasky ${MCP_URL}`
  const keyClaude = `claude mcp add --transport http heytasky ${MCP_URL} --header "Authorization: Bearer ${sampleKey}"`
  const oauthJson = `{
  "mcpServers": {
    "heytasky": {
      "url": "${MCP_URL}"
    }
  }
}`
  const keyJson = `{
  "mcpServers": {
    "heytasky": {
      "url": "${MCP_URL}",
      "headers": {
        "Authorization": "Bearer ${sampleKey}"
      }
    }
  }
}`

  const isClaude = client === 'Claude Code'
  const isChatGPT = client === 'ChatGPT'
  const isOauth = authMode === 'OAuth'
  // ChatGPT connects as a custom MCP plugin: it takes the bare server URL, not a
  // CLI command or JSON config, and always uses OAuth (no custom headers).
  const mcpSnippet = isChatGPT
    ? MCP_URL
    : isOauth
      ? isClaude
        ? oauthClaude
        : oauthJson
      : isClaude
        ? keyClaude
        : keyJson

  return (
    <div>
      <PanelHeading
        title="Developers"
        description="Create API keys and configure MCP access."
      />

      {/* MCP Client Configuration */}
      <div className="card p-5">
        <SectionTitle
          icon={<Plug className="h-4 w-4" />}
          title="MCP server"
          subtitle="Connect supported tools to Hey Tasky to draft, schedule and publish."
        />

        <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="inline-flex flex-wrap gap-1 rounded-xl border border-line bg-panel p-1">
            {MCP_CLIENTS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setClient(c)}
                className={`rounded-lg px-3 py-1.5 text-xs font-medium transition ${
                  client === c
                    ? 'bg-surface text-ink-900 border border-line shadow-card'
                    : 'text-ink-500 hover:text-ink-900'
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          <div className="inline-flex rounded-xl border border-line bg-panel p-1">
            {(['OAuth', 'API key'] as McpAuth[]).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setAuthMode(m)}
                className={`rounded-lg px-3 py-1.5 text-xs font-medium transition ${
                  authMode === m
                    ? 'bg-surface text-ink-900 border border-line shadow-card'
                    : 'text-ink-500 hover:text-ink-900'
                }`}
              >
                {m}
              </button>
            ))}
          </div>
        </div>

        <CodeBlock copyValue={mcpSnippet} copyLabel="Copy" code={<code>{mcpSnippet}</code>} />
        <p className="mt-2 text-xs text-ink-400">
          {isChatGPT
            ? 'At chatgpt.com/plugins, click the plus button, then "Add custom MCP server". Paste this URL, choose OAuth, and sign in to Hey Tasky. Web only.'
            : isOauth
              ? 'OAuth is the recommended door: your client opens a browser, you sign in with Hey Tasky and approve once. No secret to paste.'
              : isClaude
                ? 'Headless option: create an API key below and paste it here.'
                : `Headless option: add this to your ${client} MCP config and use an API key from below.`}
        </p>
        <Link
          to="/docs/mcp"
          className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-brand-500 hover:text-brand-600"
        >
          Full setup guide for every client <ExternalLink className="h-3 w-3" />
        </Link>
      </div>

      {/* API Keys */}
      <div className="card mt-4 p-5">
        <SectionTitle
          icon={<KeyRound className="h-4 w-4" />}
          title="API keys"
          subtitle="For automation platforms (n8n, Make, Zapier) and headless MCP clients."
        />

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Key name (e.g. n8n production)"
            className="flex-1 rounded-xl border border-line bg-panel px-3.5 py-2.5 text-sm outline-none focus:border-brand-400"
          />
          <button
            type="button"
            onClick={create}
            disabled={creating}
            className="btn btn-primary px-3.5 py-2.5 text-xs disabled:opacity-60"
          >
            {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Create key
          </button>
        </div>

        {error && <p className="mt-3 text-xs text-accent-red">{error}</p>}

        {fresh && (
          <div className="mt-3 rounded-xl border border-brand-200 bg-brand-50 p-3.5">
            <p className="mb-2 text-xs font-medium text-brand-700">
              Copy this key now. You will not be able to see it again.
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 truncate rounded-lg border border-brand-200 bg-surface px-3 py-2 font-mono text-sm text-ink-800">
                {fresh.key}
              </code>
              <CopyButton value={fresh.key} label="Copy" className="btn-ghost px-3 py-2" />
            </div>
          </div>
        )}

        <div className="mt-4">
          {loading ? (
            <p className="flex items-center gap-2 text-sm text-ink-400">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading keys...
            </p>
          ) : keys.length === 0 ? (
            <p className="text-sm text-ink-400">No API keys yet. Create one to get started.</p>
          ) : (
            <ul className="divide-y divide-line rounded-xl border border-line">
              {keys.map((k) => (
                <li key={k.id} className="flex items-center gap-3 px-3.5 py-3">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-ink-900">{k.name}</p>
                    <p className="truncate font-mono text-xs text-ink-400">
                      {k.prefix}... · created {fmtDate(k.createdAt)} · last used {fmtDate(k.lastUsedAt)}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => revoke(k.id)}
                    className="btn-ghost px-2.5 py-1.5 text-xs text-accent-red hover:bg-accent-red/10"
                    aria-label="Revoke key"
                  >
                    <Trash2 className="h-4 w-4" />
                    Revoke
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="mt-3 flex items-center gap-1.5 text-xs text-ink-400">
          <span>Revoking a key takes effect immediately.</span>
          <a
            href="https://modelcontextprotocol.io/docs/tutorials/security/authorization"
            target="_blank"
            rel="noreferrer"
            className="ml-auto inline-flex items-center gap-1 text-brand-500 hover:text-brand-600"
          >
            MCP docs <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      </div>

      {/* Approved Apps */}
      <div className="card mt-4 p-5">
        <SectionTitle
          icon={<Shield className="h-4 w-4" />}
          title="Approved Apps"
          subtitle="Third-party apps with access to your workspace."
        />
        <p className="text-sm text-ink-400">No third-party apps have access yet.</p>
        {channels.length > 0 && (
          <p className="mt-2 text-xs text-ink-300">
            {channels.length} connected {channels.length === 1 ? 'channel' : 'channels'} available to
            the API.
          </p>
        )}
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

export function SettingsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const tabParam = searchParams.get('tab')
  const initialTab = TABS.some((tab) => tab.id === tabParam) ? (tabParam as TabId) : 'Global'
  const [active, setActive] = useState<TabId>(initialTab)

  useEffect(() => {
    if (TABS.some((tab) => tab.id === tabParam) && tabParam !== active) {
      setActive(tabParam as TabId)
    }
  }, [active, tabParam])

  const selectTab = (tab: TabId) => {
    setActive(tab)
    setSearchParams(tab === 'Global' ? {} : { tab })
  }

  const panel = useMemo(() => {
    switch (active) {
      case 'Global':
        return <GlobalPanel />
      case 'Billing':
        return <BillingPanel />
      case 'Team':
        return <TeamPanel />
      case 'Webhooks':
        return <WebhooksPanel />
      case 'Auto Post':
        return <AutoPostPanel />
      case 'Developers':
        return <DevelopersPanel />
      default:
        return null
    }
  }, [active])

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title="Settings"
        subtitle="Manage your workspace"
        icon={<SettingsIcon className="h-5 w-5" />}
      />
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden md:flex-row">
        {/* Left tab nav */}
        <nav aria-label="Settings sections" className="flex shrink-0 gap-1 overflow-x-auto border-b border-line p-2 md:block md:w-56 md:space-y-1 md:border-b-0 md:border-r md:p-3">
          {TABS.map((tab) => {
            const isActive = active === tab.id
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => selectTab(tab.id)}
                className={`flex shrink-0 items-center gap-2.5 whitespace-nowrap rounded-xl md:w-full px-3 py-2.5 text-sm font-medium transition ${
                  isActive
                    ? 'bg-panel text-ink-900'
                    : 'text-ink-500 hover:bg-panel hover:text-ink-900'
                }`}
              >
                <span className={isActive ? 'text-brand-500' : 'text-ink-400'}>{tab.icon}</span>
                {tab.label}
                {tab.id === 'Developers' && (
                  <span className="ml-auto rounded-full bg-brand-50 px-1.5 py-0.5 text-[10px] font-semibold text-brand-600">
                    API
                  </span>
                )}
              </button>
            )
          })}
        </nav>

        {/* Right content */}
        <div className="min-h-0 min-w-0 max-w-3xl flex-1 overflow-y-auto p-4 md:p-6">{panel}</div>
      </div>
    </div>
  )
}
